import React, { useEffect, useRef, useState } from 'react';
import { Animated, TouchableWithoutFeedback, View, Keyboard, ScrollView } from 'react-native';
import { Portal, Drawer } from 'react-native-paper';
import { useThemeContext } from '../../contexts';
import { DeviceType, useResponsive } from '../hooks';

type AnimatedDrawerProps = {
  visible: boolean;
  topOffset: number;
  width: number;
  maxWidth?: number;
  menuItems: string[];
  selectedItem: string;
  onClose: () => void;
  onSelect: (item: string) => void;
};

export const AnimatedDrawer = ({
  visible,
  topOffset,
  width,
  maxWidth,
  menuItems,
  selectedItem,
  onClose,
  onSelect,
}: AnimatedDrawerProps) => {
  const { theme } = useThemeContext();
  const { screenWidth, screenHeight, deviceType } = useResponsive();
  const [shouldRender, setShouldRender] = useState(visible);
  const anim = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef<ScrollView>(null);
  const itemLayouts = useRef<Record<string, number>>({}).current;
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [drawerLeft, setDrawerLeft] = useState((screenWidth - width) / 2);
  const [openDirectionUp, setOpenDirectionUp] = useState(false);

  const effectiveMaxWidth = deviceType === DeviceType.MOBILE ? screenWidth : (maxWidth ?? 400);

  useEffect(() => {
    setDrawerLeft((screenWidth - width) / 2);
  }, [screenWidth, width]);

  useEffect(() => {
    if (visible) {
      const availableBelow = screenHeight - topOffset;
      const estimatedDrawerHeight = 230;

      if (availableBelow < estimatedDrawerHeight) {
        setOpenDirectionUp(true);
      } else {
        setOpenDirectionUp(false);
      }

      setShouldRender(true);
      Animated.timing(anim, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(anim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        setShouldRender(false);
        setInitialScrollDone(false);
      });
    }
  }, [visible, topOffset, screenHeight]);

  if (!shouldRender) return null;

  return (
    <Portal>
      <TouchableWithoutFeedback
        onPress={() => {
          Keyboard.dismiss();
          onClose();
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 998,
          }}
        >
          <Animated.View
            style={{
              position: 'absolute',
              top: openDirectionUp ? (topOffset - 325) : topOffset,
              left: drawerLeft,
              minWidth: 200,
              maxWidth: effectiveMaxWidth,
              width: width,
              zIndex: 999,
              opacity: anim,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.1,
              shadowRadius: 8,
              elevation: 8,
              backgroundColor: theme.colors.background,
              transform: [
                {
                  translateY: anim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-10, 0],
                  }),
                },
              ],
            }}
          >
            <View
              style={{
                backgroundColor: '#FFFFFF',
                borderWidth: 1,
                borderColor: theme.colors.primary,
                borderRadius: 5,
                maxHeight: 230,
                overflow: 'scroll',
              }}
            >
              <ScrollView ref={scrollRef}>
                <Drawer.Section>
                  {menuItems.map((item) => (
                    <Drawer.Item
                      key={item}
                      label={item}
                      active={item === selectedItem}
                      onPress={() => onSelect(item)}
                      onLayout={(e) => {
                        const y = e.nativeEvent.layout.y;
                        itemLayouts[item] = y;

                        if (item === selectedItem && scrollRef.current && !initialScrollDone) {
                          scrollRef.current.scrollTo({ y, animated: false });
                          setInitialScrollDone(true);
                        }
                      }}
                    />
                  ))}
                </Drawer.Section>
              </ScrollView>
            </View>
          </Animated.View>
        </View>
      </TouchableWithoutFeedback>
    </Portal>
  );
};
